import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { DateTime } from 'luxon';
import { ToastRef, ToastrService } from 'ngx-toastr';

import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { CartService } from '../../shared/services/cart.service';
import { ComponentBase } from '../component.base';
import { AuthService } from './auth.service';

@Injectable({
	providedIn: 'root',
})
export class InactivityMonitorService extends ComponentBase {
	toast: ToastRef<any>;

	constructor(
		private idle: Idle,
		private authService: AuthService,
		private toastr: ToastrService,
		private dialog: MatDialog,
		public cartService: CartService,
	) {
		super();

		idle.setIdle(57.5 * 60); // Timeout warning after 57.5 minutes of idle
		idle.setTimeout(2.5 * 60); // Timeout 2.5 minutes after warning
		idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

		idle.onIdleEnd.subscribe(() => this.hideTimeoutWarning());
		idle.onTimeoutWarning.subscribe((seconds) => this.showTimeoutWarning(seconds));
		idle.onTimeout.subscribe(() => this.timedOut());
	}

	start(): void {
		const nowTime = DateTime.now();
		const authTime = this.authService.userData
			? DateTime.fromMillis(+this.authService.userData.auth_time * 1000)
			: nowTime;
		const expiryTime = DateTime.fromMillis(+localStorage.getItem('ng2Idle.main.expiry'));

		if (expiryTime < nowTime && authTime < expiryTime) {
			this.timedOut();
		} else {
			this.idle.watch();
		}
	}

	stop(): void {
		this.idle.stop();
	}

	showTimeoutWarning(seconds: number): void {
		if (this.toast == null) {
			this.toast = this.toastr.show(
				'Move mouse or press any key to continue',
				'Your session is about to expire',
				{
					timeOut: seconds * 1000,
					disableTimeOut: 'extendedTimeOut',
					tapToDismiss: false,
				},
				'toast-action toast-icon-clock',
			).toastRef;
		}
	}

	hideTimeoutWarning() {
		if (this.toast != null) {
			this.toast.manualClose();
			this.toast = null;
		}
	}

	timedOut(): void {
		this.hideTimeoutWarning();

		const dialog = this.dialog.open(ConfirmDialogComponent, {
			width: '550px',
			data: {
				title: 'Session expired',
				hideCancel: true,
				message: 'You have been logged out due to inactivity. To sign in again please return to the Marketplace',
				buttonText: 'Close',
			},
			closeOnNavigation: false,
			disableClose: true,
		});

		dialog.afterClosed().subscribe(() => {
			this.cartService.clearLocalCart();
			this.authService.logout();
		});
	}
}
